/**
 * Per-preset Hero + Category-Grid components for the storefront homepage.
 *
 * Six visually distinct layouts. They share data shape (categories, branding,
 * trending) but render structurally different markup — not just different
 * colours — so each preset feels like a different brand altogether.
 *
 * Each hero respects optional tenant-saved overrides on `branding`:
 *   • hero_image_url, hero_eyebrow, hero_title, hero_subtitle
 *   • hero_cta_primary_label / _link, hero_cta_secondary_label / _link
 * Defaults are the curated copy each preset shipped with originally.
 */
import React from "react";
import { Link } from "react-router-dom";
import ProductCard from "../../../components/store/ProductCard";
import { imgUrl } from "../../../lib/api";

const HERO_BG = "https://images.pexels.com/photos/33101418/pexels-photo-33101418.jpeg";
const HERO_BG2 = "https://images.pexels.com/photos/36489477/pexels-photo-36489477.jpeg";
const HERO_BG3 = "https://images.unsplash.com/photo-1740431377901-c2f28d50c759?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const PLACEHOLDER_CATEGORY_IMG = "https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=800";

/** Resolve the hero background, honouring tenant override if provided. */
function heroImageFor(branding, fallback = HERO_BG) {
  const raw = (branding?.hero_image_url || "").trim();
  if (!raw) return fallback;
  // Bare S3 keys come through here (the upload pipeline writes those);
  // imgUrl() prefixes the CDN host. Full http(s) URLs pass through.
  return imgUrl(raw);
}

/**
 * Renders a tenant-overridden hero title. The saved string supports `\n`
 * for line breaks and `*word*` for accent-coloured emphasis (matching the
 * `<em>` styling each preset uses). Returns a React fragment.
 */
function renderHeroTitle(title, AccentTag = "em") {
  if (!title) return null;
  const lines = title.split(/\r?\n/);
  return lines.map((line, i) => {
    const parts = line.split(/\*([^*]+)\*/g); // odd indices are accent
    return (
      <React.Fragment key={i}>
        {parts.map((p, j) =>
          j % 2 === 1
            ? <AccentTag key={j} className="italic text-[hsl(var(--kb-accent))]">{p}</AccentTag>
            : <React.Fragment key={j}>{p}</React.Fragment>
        )}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

function pickCategoryImg(c) {
  // Route category images through `imgUrl` so both bare S3 keys AND
  // absolute (Unsplash/Pexels/CDN) URLs render correctly. Previously
  // this returned `c.image_url` raw — bare S3 keys 404'd against the
  // storefront root, and any tenant whose theme used a hero variant
  // (KalkiHero et al) got broken category tiles unless their images
  // happened to be absolute URLs.
  const resolved = c.image_url ? imgUrl(c.image_url) : "";
  return resolved || PLACEHOLDER_CATEGORY_IMG;
}

/* ──────────────────────────────────────────────────────────────────
 * KALKI — Editorial Indian Luxury (existing layout, preserved)
 *  • Full-bleed hero, italic serif headline, 2x2 bento grid
 * ────────────────────────────────────────────────────────────────── */

export function KalkiHero({ branding }) {
  const eyebrow = branding?.hero_eyebrow || branding?.tagline || "The Bridal Edit · Autumn–Winter";
  const subtitle = branding?.hero_subtitle || "A marketplace for India's finest independent ateliers — from Banarasi looms to Chikankari studios.";
  const cta1Label = branding?.hero_cta_primary_label || "Explore the Bazaar";
  const cta1Link = branding?.hero_cta_primary_link || "/shop";
  const cta2Label = branding?.hero_cta_secondary_label || "Sell Your Craft";
  const cta2Link = branding?.hero_cta_secondary_link || "/seller/onboard";
  return (
    <section className="relative h-[85vh] md:h-[92vh] overflow-hidden" data-testid="hero-kalki">
      <img src={heroImageFor(branding, HERO_BG)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
        <div className="overline text-white/90 mb-6">{eyebrow}</div>
        <h1 className="font-display text-5xl sm:text-7xl lg:text-[110px] leading-[0.95] max-w-5xl font-light tracking-tighter">
          {branding?.hero_title ? renderHeroTitle(branding.hero_title) : (<>Worn by stories.<br /><em className="italic text-[hsl(var(--kb-accent))]">Woven by hands.</em></>)}
        </h1>
        <p className="max-w-xl mt-8 text-white/80 text-lg leading-relaxed">{subtitle}</p>
        <div className="flex gap-4 mt-10">
          <Link to={cta1Link} className="kb-btn-primary" data-testid="hero-shop-btn">{cta1Label}</Link>
          {cta2Label && <Link to={cta2Link} className="kb-btn-outline border-white text-white hover:bg-white/10" data-testid="hero-sell-btn">{cta2Label}</Link>}
        </div>
      </div>
    </section>
  );
}

export function KalkiCategoryGrid({ categories }) {
  return (
    <section className="px-6 md:px-12 lg:px-24 py-20" data-testid="cat-kalki">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline mb-3">Shop by Edit</div>
          <h2 className="font-display text-4xl lg:text-5xl">Timeless silhouettes,<br />curated categories.</h2>
        </div>
        <Link to="/shop" className="hidden md:inline text-sm uppercase tracking-widest underline">View all →</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {categories.filter((c) => !c.parent_id).slice(0, 4).map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="relative overflow-hidden group aspect-[3/4]">
            <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-4 left-4 text-white">
              <div className="overline text-white/80">Shop</div>
              <div className="font-display text-2xl md:text-3xl">{c.name}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * MYNTRA — Bright e-commerce: small banner + circular category icons + product carousel
 * ────────────────────────────────────────────────────────────────── */

export function MyntraHero({ branding }) {
  const eyebrow = branding?.hero_eyebrow || branding?.tagline || "FLAT 50–70% OFF · NEW ARRIVALS LIVE";
  const customImg = (branding?.hero_image_url || "").trim() ? imgUrl(branding.hero_image_url) : null;
  const banners = [customImg || HERO_BG, HERO_BG3, HERO_BG2];
  return (
    <section className="relative" data-testid="hero-myntra">
      <div className="bg-gradient-to-r from-[hsl(var(--kb-primary))] to-[hsl(var(--kb-accent))] py-3 text-white text-center text-sm font-semibold tracking-wide">
        {eyebrow} · USE CODE STYLEUP
      </div>
      <div className="grid md:grid-cols-3 gap-1">
        {banners.map((img, i) => (
          <Link key={i} to={branding?.hero_cta_primary_link || "/shop"} className="relative aspect-[16/10] overflow-hidden group">
            <img src={img} alt="" className="w-full h-full object-cover kb-product-img" />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />
            <div className="absolute inset-x-6 bottom-6 text-white">
              <div className="text-xs font-bold tracking-widest opacity-80">{["MIN 60% OFF", "TRENDING NOW", "DESIGNER EDITS"][i]}</div>
              <div className="font-display text-3xl mt-2 leading-tight">{i === 0 && branding?.hero_title ? renderHeroTitle(branding.hero_title) : ["Festive Edit", "Daily Wear", "Bridal Couture"][i]}</div>
              <div className="mt-3 inline-block bg-white text-[hsl(var(--kb-primary))] px-4 py-2 text-xs font-bold uppercase">{i === 0 && branding?.hero_cta_primary_label ? branding.hero_cta_primary_label : "Shop now"}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function MyntraCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 8);
  return (
    <section className="px-4 md:px-8 py-10 bg-[hsl(var(--kb-surface-alt))]" data-testid="cat-myntra">
      <div className="text-center mb-6">
        <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-wide">Shop by Category</h2>
      </div>
      <div className="flex md:grid md:grid-cols-8 gap-4 overflow-x-auto kb-scroll-hide pb-3 -mx-4 px-4 md:mx-0 md:px-0">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="flex-shrink-0 w-24 md:w-auto flex flex-col items-center text-center group">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-transparent group-hover:border-[hsl(var(--kb-primary))] transition">
              <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            </div>
            <div className="text-xs font-semibold mt-2 uppercase">{c.name}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * AJIO — Sporty modern: split-screen hero, bold sans-serif, horizontal scroll chips
 * ────────────────────────────────────────────────────────────────── */

export function AjioHero({ branding }) {
  const eyebrow = branding?.hero_eyebrow || branding?.tagline || "DROP 04 · IN STORE NOW";
  const subtitle = branding?.hero_subtitle || "The new edit is live. 240+ pieces from 18 ateliers. Get there before everyone else does.";
  const cta1Label = branding?.hero_cta_primary_label || "Shop the drop →";
  const cta1Link = branding?.hero_cta_primary_link || "/shop";
  const cta2Label = branding?.hero_cta_secondary_label || "Watch reels";
  const cta2Link = branding?.hero_cta_secondary_link || "/reels";
  return (
    <section className="grid md:grid-cols-2 min-h-[80vh] bg-[hsl(var(--kb-primary))] text-white" data-testid="hero-ajio">
      <div className="flex flex-col justify-center p-10 md:p-20 order-2 md:order-1">
        <div className="text-xs font-bold tracking-[0.3em] text-[hsl(var(--kb-accent))] mb-6">{eyebrow}</div>
        <h1 className="font-display text-6xl md:text-7xl lg:text-8xl leading-[0.95] font-extrabold uppercase">
          {branding?.hero_title ? renderHeroTitle(branding.hero_title, "span") : (<>Move<br /><span className="text-[hsl(var(--kb-accent))]">different.</span></>)}
        </h1>
        <p className="mt-8 text-white/70 max-w-md leading-relaxed">{subtitle}</p>
        <div className="flex gap-3 mt-10">
          <Link to={cta1Link} className="bg-[hsl(var(--kb-accent))] text-black px-8 py-4 uppercase text-sm font-bold tracking-wider hover:opacity-90 transition" data-testid="hero-shop-btn">{cta1Label}</Link>
          {cta2Label && <Link to={cta2Link} className="border border-white px-8 py-4 uppercase text-sm font-bold tracking-wider hover:bg-white hover:text-black transition" data-testid="hero-sell-btn">{cta2Label}</Link>}
        </div>
      </div>
      <div className="relative aspect-square md:aspect-auto order-1 md:order-2">
        <img src={heroImageFor(branding, HERO_BG)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </section>
  );
}

export function AjioCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 6);
  return (
    <section className="py-16" data-testid="cat-ajio">
      <div className="px-6 md:px-12 mb-8 flex items-end justify-between flex-wrap gap-4">
        <h2 className="font-display text-4xl md:text-5xl font-extrabold uppercase">Edits</h2>
        <Link to="/shop" className="text-xs font-bold uppercase tracking-widest underline">All edits →</Link>
      </div>
      <div className="flex gap-3 overflow-x-auto kb-scroll-hide px-6 md:px-12 pb-3">
        {cats.map((c, i) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className={`flex-shrink-0 relative overflow-hidden group ${i % 3 === 0 ? "w-[420px] aspect-[4/5]" : "w-[280px] aspect-[3/4]"}`}>
            <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
              <div className="text-xs font-bold tracking-widest text-[hsl(var(--kb-accent))]">EDIT 0{i + 1}</div>
              <div className="font-display text-2xl md:text-3xl font-extrabold uppercase">{c.name}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * MANGO — Pure monochrome editorial: fullscreen hero, generous whitespace, masonry categories
 * ────────────────────────────────────────────────────────────────── */

export function MangoHero({ branding }) {
  const eyebrow = branding?.hero_eyebrow || branding?.tagline || "Spring · Summer 26";
  const cta1Label = branding?.hero_cta_primary_label || "Discover the collection";
  const cta1Link = branding?.hero_cta_primary_link || "/shop";
  return (
    <section className="relative h-screen overflow-hidden bg-white" data-testid="hero-mango">
      <img src={heroImageFor(branding, HERO_BG)} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
      <div className="absolute inset-0 bg-white/10" />
      <div className="relative z-10 h-full flex flex-col items-end justify-end text-right text-white p-10 md:p-20">
        <div className="text-xs uppercase tracking-[0.4em] mb-6 opacity-80">{eyebrow}</div>
        <h1 className="font-display text-7xl md:text-8xl lg:text-[140px] leading-[0.85] font-extrabold tracking-tighter max-w-3xl">
          {branding?.hero_title ? renderHeroTitle(branding.hero_title) : (<>The art<br />of less.</>)}
        </h1>
        {branding?.hero_subtitle && <p className="mt-8 max-w-md text-white/85 leading-relaxed">{branding.hero_subtitle}</p>}
        <Link to={cta1Link} className="mt-12 border-b border-white text-sm uppercase tracking-[0.3em] pb-2 hover:tracking-[0.4em] transition-all" data-testid="hero-shop-btn">{cta1Label}</Link>
      </div>
    </section>
  );
}

export function MangoCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 5);
  if (!cats.length) return null;
  return (
    <section className="px-6 md:px-12 lg:px-32 py-32" data-testid="cat-mango">
      <div className="text-center mb-20">
        <div className="text-xs uppercase tracking-[0.4em] text-[hsl(var(--kb-muted))] mb-4">Selection</div>
        <h2 className="font-display text-5xl md:text-7xl font-light">Curated essentials.</h2>
      </div>
      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {cats.slice(0, 5).map((c, i) => {
          const layouts = [
            "col-span-12 md:col-span-7 aspect-[16/9]",
            "col-span-12 md:col-span-5 aspect-[4/5]",
            "col-span-6 md:col-span-4 aspect-square",
            "col-span-6 md:col-span-4 aspect-square",
            "col-span-12 md:col-span-4 aspect-square",
          ];
          return (
            <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                  className={`relative overflow-hidden group ${layouts[i]}`}>
              <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img grayscale group-hover:grayscale-0 transition duration-700" />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/60 to-transparent text-white">
                <div className="font-display text-3xl md:text-4xl">{c.name}</div>
                <div className="text-xs uppercase tracking-[0.3em] opacity-80 mt-1">View collection</div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * BIBA — Warm Indian ethnic: festive overlay hero, rounded cream cards
 * ────────────────────────────────────────────────────────────────── */

export function BibaHero({ branding }) {
  const eyebrow = branding?.hero_eyebrow || branding?.tagline || "Festive Wardrobe '26";
  const subtitle = branding?.hero_subtitle || "Hand-picked anarkalis, kurtis & saree sets — crafted in our karkhanas, ready for your festive nights.";
  const cta1Label = branding?.hero_cta_primary_label || "Shop festive";
  const cta1Link = branding?.hero_cta_primary_link || "/shop";
  const cta2Label = branding?.hero_cta_secondary_label || "Our karkhanas";
  const cta2Link = branding?.hero_cta_secondary_link || "/seller/onboard";
  return (
    <section className="relative h-[85vh] overflow-hidden bg-[hsl(var(--kb-surface-alt))]" data-testid="hero-biba">
      <img src={heroImageFor(branding, HERO_BG)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-l from-[hsl(var(--kb-primary))]/85 via-[hsl(var(--kb-primary))]/40 to-transparent" />
      <div className="relative z-10 h-full flex items-center justify-end px-6 md:px-20">
        <div className="max-w-xl text-right text-white">
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--kb-accent))] mb-4">{eyebrow}</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-tight">
            {branding?.hero_title ? renderHeroTitle(branding.hero_title) : (<>Celebrate<br />in <em className="italic text-[hsl(var(--kb-accent))]">colour.</em></>)}
          </h1>
          <p className="mt-6 text-white/85 leading-relaxed">{subtitle}</p>
          <div className="flex gap-3 mt-8 justify-end flex-wrap">
            <Link to={cta1Link} className="bg-white text-[hsl(var(--kb-primary))] px-7 py-3 uppercase text-xs font-bold tracking-wider rounded-full hover:bg-[hsl(var(--kb-accent))] hover:text-white transition" data-testid="hero-shop-btn">{cta1Label}</Link>
            {cta2Label && <Link to={cta2Link} className="border border-white text-white px-7 py-3 uppercase text-xs font-bold tracking-wider rounded-full hover:bg-white/10 transition" data-testid="hero-sell-btn">{cta2Label}</Link>}
          </div>
        </div>
      </div>
    </section>
  );
}

export function BibaCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 6);
  return (
    <section className="px-6 md:px-12 py-20 bg-[hsl(var(--kb-bg))]" data-testid="cat-biba">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--kb-primary))] mb-2">Shop by occasion</div>
        <h2 className="font-display text-4xl md:text-5xl">From <em className="italic text-[hsl(var(--kb-primary))]">mehendi</em> to <em className="italic text-[hsl(var(--kb-primary))]">reception</em>.</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[hsl(var(--kb-border))] group hover:shadow-md transition">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            </div>
            <div className="p-4 text-center">
              <div className="font-display text-xl text-[hsl(var(--kb-primary))]">{c.name}</div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--kb-muted))] mt-1">Explore →</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * NEERUS — Couture jewel: dark ornate hero, gold accent borders, 4-col luxury grid
 * ────────────────────────────────────────────────────────────────── */

export function NeerusHero({ branding }) {
  const eyebrow = branding?.hero_eyebrow || branding?.tagline || "Maharani Couture · 2026";
  const subtitle = branding?.hero_subtitle || "Hand-embroidered. Bench-made. Dressed by India's most exacting couturiers, exclusively for you.";
  const cta1Label = branding?.hero_cta_primary_label || "Discover Couture";
  const cta1Link = branding?.hero_cta_primary_link || "/shop";
  const cta2Label = branding?.hero_cta_secondary_label || "Visit Atelier";
  const cta2Link = branding?.hero_cta_secondary_link || "/seller/onboard";
  return (
    <section className="relative h-[90vh] overflow-hidden bg-[hsl(var(--kb-primary))]" data-testid="hero-neerus">
      <img src={heroImageFor(branding, HERO_BG)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--kb-primary))]/70 via-[hsl(var(--kb-primary))]/40 to-[hsl(var(--kb-primary))]" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
        <div className="w-px h-16 bg-[hsl(var(--kb-accent))] mb-8" />
        <div className="text-xs tracking-[0.5em] uppercase text-[hsl(var(--kb-accent))] mb-6">{eyebrow}</div>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-tight uppercase tracking-wide" style={{ letterSpacing: "0.04em" }}>
          {branding?.hero_title ? renderHeroTitle(branding.hero_title, "span") : (<>The Heirloom<br /><span className="text-[hsl(var(--kb-accent))]">Atelier</span></>)}
        </h1>
        <div className="w-12 h-px bg-[hsl(var(--kb-accent))] my-8" />
        <p className="max-w-lg text-white/80 leading-relaxed">{subtitle}</p>
        <div className="flex gap-4 mt-10">
          <Link to={cta1Link} className="bg-[hsl(var(--kb-accent))] text-[hsl(var(--kb-primary))] px-10 py-4 uppercase text-xs font-semibold tracking-[0.3em] hover:opacity-90" data-testid="hero-shop-btn">{cta1Label}</Link>
          {cta2Label && <Link to={cta2Link} className="border border-[hsl(var(--kb-accent))] text-[hsl(var(--kb-accent))] px-10 py-4 uppercase text-xs font-semibold tracking-[0.3em] hover:bg-[hsl(var(--kb-accent))]/10" data-testid="hero-sell-btn">{cta2Label}</Link>}
        </div>
      </div>
    </section>
  );
}

export function NeerusCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 4);
  return (
    <section className="px-6 md:px-12 py-24 bg-[hsl(var(--kb-bg))]" data-testid="cat-neerus">
      <div className="text-center mb-16">
        <div className="w-12 h-px bg-[hsl(var(--kb-accent))] mx-auto mb-6" />
        <div className="text-xs uppercase tracking-[0.4em] text-[hsl(var(--kb-primary))] mb-3">The Edits</div>
        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-wider" style={{ letterSpacing: "0.05em" }}>
          Couture, <em className="italic text-[hsl(var(--kb-accent))]">curated.</em>
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="relative overflow-hidden group aspect-[3/4] border border-[hsl(var(--kb-accent))]/40 hover:border-[hsl(var(--kb-accent))] transition">
            <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--kb-primary))] via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 text-white text-center">
              <div className="w-8 h-px bg-[hsl(var(--kb-accent))] mx-auto mb-3" />
              <div className="font-display text-xl md:text-2xl uppercase tracking-widest">{c.name}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Trending product grid variants per preset (subtle tweaks)
 * ────────────────────────────────────────────────────────────────── */

export function TrendingGrid({ products, preset, loading, error }) {
  const wrapClass = {
    myntra: "px-4 md:px-8 py-10 bg-white",
    ajio: "px-6 md:px-12 py-16",
    mango: "px-6 md:px-12 lg:px-32 py-32 bg-[hsl(var(--kb-bg))]",
    biba: "px-6 md:px-12 py-16 bg-[hsl(var(--kb-surface-alt))]",
    neerus: "px-6 md:px-12 py-24",
    kalki: "px-6 md:px-12 lg:px-24 py-20",
  }[preset] || "px-6 md:px-12 lg:px-24 py-20";

  const headers = {
    myntra: { eyebrow: "Trending now", title: "Bestsellers this week", view: "Shop more →" },
    ajio: { eyebrow: "DROP 04", title: "JUST IN.", view: "All drops →" },
    mango: { eyebrow: "New arrivals", title: "This season's edits.", view: "View all" },
    biba: { eyebrow: "Just arrived", title: "Fresh from our karkhanas", view: "See everything →" },
    neerus: { eyebrow: "Couture journal", title: "Artisan-made, ready to wear.", view: "Atelier shop →" },
    kalki: { eyebrow: "Just Arrived", title: "New from our ateliers.", view: "See everything →" },
  }[preset] || {};

  const cols = preset === "myntra" || preset === "neerus" ? "md:grid-cols-4" : preset === "mango" ? "md:grid-cols-3" : "md:grid-cols-4";

  return (
    <section className={wrapClass}>
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-3">{headers.eyebrow}</div>
          <h2 className="font-display text-4xl lg:text-5xl">{headers.title}</h2>
        </div>
        <Link to="/shop" className="text-sm uppercase tracking-widest underline">{headers.view}</Link>
      </div>
      {error && (
        <div className="mb-6 border border-red-300 bg-red-50 text-red-800 p-3 text-sm" data-testid="home-error">{error}</div>
      )}
      {loading ? (
        <div className={`grid grid-cols-2 ${cols} gap-6 md:gap-8`}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="py-10 text-[hsl(var(--kb-muted))]">No products available yet.</div>
      ) : (
        <div className={`grid grid-cols-2 ${cols} gap-6 md:gap-8`}>
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </section>
  );
}
